declare global {
  interface Window {
    gtag?: (command: string, targetId: string, config?: Record<string, unknown>) => void;
    dataLayer?: Array<Record<string, unknown>>;
    umami?: { track: (event: string, data?: Record<string, unknown>) => void };
  }
}

/**
 * The ONE place this app reports a lead. Both lead sources go through it — the
 * `/kontakt` → `/danke` path and the configurator modal — so the "one GTM trigger,
 * one GA4 key event" contract is structural instead of copy-maintained. Adding a
 * second inline `generate_lead` push is the defect this exists to prevent.
 *
 * Wrapped in try/catch on purpose: GTM REPLACES `dataLayer.push` with its own
 * function at runtime, and the funnel's caller awaits this inside the contact form's
 * submit `try`. Without the guard a throwing push would flip a form to its error
 * state after a POST that actually succeeded — telling the visitor their message
 * failed when it did not. Analytics must never decide whether a submit looks sent.
 *
 * Umami (cookieless, the funnel source of truth — `.claude/skills/umami/SKILL.md`)
 * rides the same helper so both sinks see exactly the same leads. Each sink has its
 * OWN guard: both are third-party code at runtime, and under one shared `try` a
 * throwing Umami script silently cost GTM its `generate_lead`.
 */
export function pushLead(params: {
  leadType: string;
  formLocation: string;
  language: string;
}): void {
  try {
    window.umami?.track('contact-submit');
  } catch {
    // Reporting is best-effort; the submit already succeeded.
  }
  try {
    window.dataLayer = window.dataLayer ?? [];
    window.dataLayer.push({
      event: 'generate_lead',
      lead_type: params.leadType,
      form_location: params.formLocation,
      language: params.language,
    });
  } catch {
    // Reporting is best-effort; the submit already succeeded.
  }
}

export const trackEvent = (eventName: string, params?: Record<string, unknown>): void => {
  if (typeof window !== 'undefined' && window.gtag) {
    window.gtag('event', eventName, params);
  }
};

export const trackFormSubmission = (formName: string): void => {
  trackEvent('form_submission', { form_name: formName });
};

export const trackPricingClick = (tier: string): void => {
  trackEvent('pricing_click', { tier });
};

export const trackCTAClick = (ctaName: string): void => {
  trackEvent('cta_click', { cta_name: ctaName });
};
